import React, {Component} from "react";
import {Control, Field, Icon} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {getFaceRecognitionResult} from "../../../api/models/FaceRecognition";
import InformationCard from "../../../elements/InformationCard";
import BeautifyDecimal from "../../../utils/BeautifyDecimal";

interface IFaceRecognitionModelState {
    loading: boolean;
    file: any,
    imageUrl: any,
    imageBase64: any,
    fileErrorMessage: string,
    result: any;
    faces: any;
    selectedResult: any;
    errorMessage: string;
    successMessage: string;
    imageWidth: any;
    imageHeight: any;
    imageNaturalWidth: any;
    imageNaturalHeight: any;
    imageOffsetLeft: any;
    imageOffsetTop: any;
}

class FaceRecognitionModel extends Component<{}, IFaceRecognitionModelState> {
    public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: [],
            faces: [],
            selectedResult: null,
            errorMessage: "",
            successMessage: "",
            imageWidth: null,
            imageHeight: null,
            imageNaturalWidth: null,
            imageNaturalHeight: null,
            imageOffsetLeft: 0,
            imageOffsetTop: 0
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.FACE_RECOGNITION);
    }

    public scaleFaceRectangle = (face: any) => {
        const ratio = this.state.imageWidth / this.state.imageNaturalWidth;

        const width = face.width * ratio;
        const height = face.height * ratio;

        const top = face.y * ratio - height / 2 + this.state.imageOffsetTop;
        const left = face.x * ratio - width / 2 + this.state.imageOffsetLeft;

        return {
            top,
            left,
            width,
            height
        };
    };

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: "",
            errorMessage: "",
            successMessage: "",
            result: [],
            faces: [],
            selectedResult: null
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {
                const url = URL.createObjectURL(file);
                this.setState({
                    file,
                    imageUrl: url
                }, () => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        this.setState({
                            imageBase64: reader.result
                        });
                    };
                    reader.readAsDataURL(file);
                });
            } else {
                this.setState({
                    file: null,
                    imageUrl: "",
                    imageBase64: "",
                    fileErrorMessage: "The model only accepts JPEG or PNG format files."
                });
            }
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        let valid = true;

        if (this.state.loading) {
            valid = false;
        }

        if (!this.state.file || !this.state.imageBase64 || this.state.fileErrorMessage) {
            if (!this.state.file || !this.state.imageBase64) {
                this.setState({
                    fileErrorMessage: "Please select image."
                });
            }

            valid = false;
        }

        if (!valid) {
            this.setState({
                result: [],
                faces: [],
                selectedResult: null
            });
            return;
        }

        this.setState({
            loading: true,
            result: [],
            faces: [],
            selectedResult: null,
            errorMessage: "",
            successMessage: ""
        });

        let errorOccurred = false;

        const formData = new FormData();
        formData.append("image", this.state.file);

        const response = await getFaceRecognitionResult(formData).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            const responseData = response.data;
            if (responseData) {
                if (responseData.recognize && responseData.recognize.results && responseData.recognize.results.length) {
                    this.setState({
                        loading: false,
                        result: responseData.recognize.results
                    });
                }
                if (responseData.media && responseData.media.faces && responseData.media.faces.length) {
                    const facesCount = responseData.media.faces.length;
                    let successMessage = `${facesCount} face${facesCount > 1 ? 's' : ''} ${facesCount > 1 ? 'were' : 'was'} detected.`;
                    successMessage += 'Please click on the detected face to view matched results with confidence scores.';
                    this.setState({
                        loading: false,
                        faces: responseData.media.faces,
                        successMessage
                    });
                }
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't recognize faces."
                });
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.FACE_RECOGNITION);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: [],
            errorMessage: "",
            successMessage: "",
            imageWidth: null,
            imageHeight: null,
            imageNaturalWidth: null,
            imageNaturalHeight: null,
            imageOffsetLeft: 0,
            imageOffsetTop: 0
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <SectionName>Select Image:</SectionName>
                                <input
                                    ref={(el) => this.fileInputRef = el}
                                    type="file"
                                    accept={this.ALLOWED_FILE_TYPES_STR}
                                    onChange={this.handleFileChange}
                                    style={this.fileInputStyle}
                                />
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                {
                                    !this.state.fileErrorMessage && this.state.file && this.state.imageUrl ?
                                        <div style={{
                                            margin: '20px',
                                            textAlign: 'center',
                                            position: 'relative'
                                        }}>
                                            {
                                                this.state.faces.length ? <React.Fragment>
                                                    {
                                                        this.state.faces.map((face: any, index: any) => {
                                                            let isSelected = false;
                                                            if (this.state.selectedResult) {
                                                                isSelected = face.face_uuid === this.state.selectedResult.face_uuid;
                                                            }
                                                            const position = this.scaleFaceRectangle(face);
                                                            const borderStyle = isSelected ? '3px solid white' : '2px solid gold';
                                                            const boxShadowStyle = isSelected ? '2px 2px black' : '1px 1px black';
                                                            return <div
                                                                key={index}
                                                                style={{
                                                                    border: borderStyle,
                                                                    boxShadow: boxShadowStyle,
                                                                    position: 'absolute',
                                                                    top: position.top + 'px',
                                                                    left: position.left + 'px',
                                                                    width: position.width + 'px',
                                                                    height: position.height + 'px',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => {
                                                                    const found = this.state.result.find((f: any) => f.face_uuid === face.face_uuid);
                                                                    if (found) {
                                                                        this.setState({
                                                                            selectedResult: found
                                                                        });
                                                                    } else {
                                                                        this.setState({
                                                                            selectedResult: null
                                                                        });
                                                                    }
                                                                }}
                                                            />;
                                                        })
                                                    }
                                                </React.Fragment> : <></>
                                            }
                                            <img
                                                style={{
                                                    pointerEvents: 'none'
                                                }}
                                                alt="Image"
                                                src={this.state.imageUrl}
                                                onLoad={(e: any) => {
                                                    const image = e.target;
                                                    this.setState({
                                                        imageWidth: image.width,
                                                        imageHeight: image.height,
                                                        imageNaturalWidth: image.naturalWidth,
                                                        imageNaturalHeight: image.naturalHeight,
                                                        imageOffsetLeft: image.offsetLeft,
                                                        imageOffsetTop: image.offsetTop
                                                    });
                                                }}
                                            />
                                        </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.errorMessage ?
                                <div>{this.state.errorMessage}</div> : <></>
                        }
                        {
                            !this.state.loading && this.state.successMessage ?
                                <div>{this.state.successMessage}</div> : <></>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.selectedResult && this.state.selectedResult.matches ? <InformationCard>
                                <ul style={{listStyleType: 'disc'}}>
                                    {
                                        this.state.selectedResult.matches.map((face: any, matchIndex: number) => {
                                            return <li style={{marginLeft: '20px'}} key={matchIndex}>
                                                <span>{face.person_id}{" - "}{BeautifyDecimal(face.confidence)}</span>
                                            </li>;
                                        })
                                    }
                                </ul>
                            </InformationCard> : <></>
                        }
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default FaceRecognitionModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/FaceRecognition/FaceRecognitionModel.tsx